/**
 * LCP do cardápio, medido em campo.
 *
 * ## Por que existe
 *
 * O LCP aparece dezenas de vezes em comentário neste repositório — o
 * `image-tag` foi trocado por `<img>` por causa dele, o embedding do cardápio no
 * HTML foi justificado com "−43%", o `menu-list` renderiza em duas fases para
 * ancorá-lo. Nada disso é medido em produção: todos os números vêm do
 * Lighthouse, que roda numa máquina nossa, numa rede nossa.
 *
 * Isso vira bloqueante quando se propõe **pré-carregar os chunks do checkout**.
 * O ganho da proposta é conhecido (a população que perde chunk em passo de
 * dinheiro); o CUSTO é justamente competir por rede com as imagens do cardápio,
 * que são a candidata a LCP. Sem medida de campo, a troca é inavaliável — e
 * medir DEPOIS de mexer não serve, porque não haveria contra o quê comparar.
 *
 * Por isso este arquivo entra ANTES do prefetch: ele planta a régua no chão
 * enquanto o terreno ainda está intocado.
 *
 * ## O que ele NÃO faz
 *
 * Não mede o LCP "do app". Mede o do documento corrente, que no caminho normal
 * é o cardápio. Numa sessão que entra direto no checkout o número descreve
 * aquela tela, e quem for ler precisa estratificar por rota — é o mesmo cuidado
 * que `route` já exige no `ClientAppError`.
 *
 * ## Cuidados de medição
 *
 * - **`buffered: true`** é obrigatório: o maior elemento costuma pintar antes de
 *   qualquer código nosso rodar. Sem isso mediríamos só as sessões lentas, que
 *   é exatamente o viés que tornaria o número inútil.
 * - **A ÚLTIMA entry é o LCP**, não a primeira: o candidato é revisado para cima
 *   conforme elementos maiores pintam. O browser para de emitir sozinho na
 *   primeira interação do usuário, que é a definição da métrica.
 * - **Reportar no fim, nunca no meio.** Ler o valor quando o `ClientMenuViewed`
 *   sai daria o LCP parcial: aquele evento é emitido quando o DADO chega, e as
 *   imagens ainda não pintaram. Daí o gatilho ser página escondida / descarga,
 *   com um prazo de segurança.
 */

/** Só o que este observador usa do browser — facilita o teste. */
export interface JanelaLcp {
  PerformanceObserver?: any;
  document?: any;
  addEventListener?: (tipo: string, fn: any, opts?: any) => void;
}

/** Leitura final do LCP. Todos os campos são opcionais: ausente ≠ zero. */
export interface LeituraLcp {
  /** Milissegundos do início da navegação até o maior elemento pintar. */
  lcp_ms?: number;
  /** `img` | `texto` — separa cardápio com foto de cardápio sem foto. */
  lcp_tipo?: string;
  /** Bytes do elemento, quando é imagem. Diz se o peso explica o tempo. */
  lcp_bytes?: number;
}

let ultimaEntrada: any = null;
let observador: any = null;
let cancelar: (() => void) | null = null;

/** `img` quando a entry aponta um elemento de imagem; `texto` caso contrário. */
function tipoDaEntrada(entrada: any): string {
  try {
    // `url` preenchida é o sinal canônico de LCP de imagem — vale para <img>,
    // <video poster> e background-image, que o `tagName` não distinguiria.
    if (entrada && typeof entrada.url === 'string' && entrada.url) return 'img';
  } catch (e) { /* entry exótica: cai em texto */ }
  return 'texto';
}

function numeroFinito(v: any): number | null {
  const n = typeof v === 'number' ? v : NaN;
  return isFinite(n) && n >= 0 ? n : null;
}

/**
 * Liga o observador. Idempotente: chamar duas vezes não duplica listener nem
 * zera o que já foi observado.
 *
 * Devolve a função de desligar (ou `null` se o motor não suporta), usada só
 * pelo teste como controle positivo de que o registro realmente aconteceu.
 */
export function observarLcp(alvo?: JanelaLcp): (() => void) | null {
  if (cancelar) return cancelar;
  try {
    const janela: any = alvo || (typeof window !== 'undefined' ? window : null);
    if (!janela || typeof janela.PerformanceObserver !== 'function') return null;

    observador = new janela.PerformanceObserver((lista: any) => {
      try {
        const entradas = lista && typeof lista.getEntries === 'function' ? lista.getEntries() : [];
        // A última: o candidato a LCP só cresce até a primeira interação.
        if (entradas && entradas.length) ultimaEntrada = entradas[entradas.length - 1];
      } catch (e) { /* callback nunca lança: ela roda dentro do motor */ }
    });
    // `buffered` entrega o que já aconteceu antes deste registro — sem ele
    // mediríamos só quem pintou depois de o bundle rodar.
    observador.observe({ type: 'largest-contentful-paint', buffered: true });

    cancelar = () => {
      try { if (observador && observador.disconnect) observador.disconnect(); } catch (e) { }
      observador = null;
      ultimaEntrada = null;
      cancelar = null;
    };
    return cancelar;
  } catch (e) {
    // `type` desconhecido (Safari < 16) lança no `observe`. Sem LCP, o campo
    // simplesmente não existe — nunca um zero que pareceria medida.
    return null;
  }
}

/**
 * O que foi observado até agora. Objeto vazio quando nada foi — e vazio é a
 * resposta certa: um `lcp_ms: 0` entraria no Athena como "pintou
 * instantaneamente" e envenenaria qualquer média.
 */
export function lerLcp(): LeituraLcp {
  const out: LeituraLcp = {};
  try {
    if (!ultimaEntrada) return out;
    const ms = numeroFinito(ultimaEntrada.startTime);
    if (ms === null) return out;
    out.lcp_ms = Math.round(ms);
    out.lcp_tipo = tipoDaEntrada(ultimaEntrada);
    const bytes = numeroFinito(ultimaEntrada.size);
    if (bytes !== null && out.lcp_tipo === 'img') out.lcp_bytes = Math.round(bytes);
  } catch (e) { /* devolve o que conseguiu montar */ }
  return out;
}

/** Só para o teste: devolve o módulo ao estado de quem nunca observou. */
export function _resetarLcpParaTeste(): void {
  ultimaEntrada = null;
  observador = null;
  cancelar = null;
}

/** Prazo de segurança: depois disto o LCP já não muda em sessão real. */
export const PRAZO_RELATORIO_MS = 15000;

let jaRelatou = false;

/**
 * Chama `relatar` UMA vez, quando o LCP puder ser considerado final.
 *
 * "Final" é o primeiro de três: a página ficou escondida, está sendo
 * descarregada, ou passaram {@link PRAZO_RELATORIO_MS}. Os dois primeiros são
 * o padrão do web-vitals — é quando o browser para de revisar o candidato e a
 * última chance de mandar qualquer coisa. O terceiro existe porque a aba que
 * nunca perde o foco (TV de balcão, monitor secundário) jamais dispararia os
 * outros dois, e a sessão inteira sumiria da amostra — um viés que deixaria
 * fora justamente as sessões longas.
 *
 * `jaRelatou` é de módulo, não de closure: um `ngOnInit` que rode duas vezes
 * (re-render, volta de rota) não pode virar dois eventos, senão a contagem por
 * sessão deixa de significar sessão.
 */
export function agendarRelatorioFinal(
  relatar: (leitura: LeituraLcp) => void,
  alvo?: JanelaLcp,
  prazoMs: number = PRAZO_RELATORIO_MS,
): void {
  try {
    if (jaRelatou) return;
    const janela: any = alvo || (typeof window !== 'undefined' ? window : null);
    if (!janela) return;

    const disparar = () => {
      if (jaRelatou) return;
      jaRelatou = true;
      try { relatar(lerLcp()); } catch (e) { /* telemetria nunca derruba a tela */ }
    };

    const doc = janela.document;
    if (doc && typeof janela.addEventListener === 'function') {
      janela.addEventListener('pagehide', disparar);
      if (typeof doc.addEventListener === 'function') {
        doc.addEventListener('visibilitychange', () => {
          if (doc.visibilityState === 'hidden') disparar();
        });
      }
    }
    if (typeof setTimeout === 'function') setTimeout(disparar, prazoMs);
  } catch (e) {
    // Sem listeners nem timer não há relatório — e não ter o dado é melhor do
    // que ter um dado que veio de um caminho que ninguém entende.
  }
}

/** Só para o teste: reabilita o relatório único. */
export function _resetarRelatorioParaTeste(): void {
  jaRelatou = false;
}
